import React, { type ComponentType } from 'react';
import { ReportErrors } from '@jira/platform__error-reporting/src/ReportErrors.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import type { ErrorBoundaryFlagProps, ErrorFlagRendererProps } from './types.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { ErrorBoundaryFlagProps } from './types';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ErrorFlagRendererLazy: ComponentType<ErrorFlagRendererProps> = lazy(
	() => import(/* webpackChunkName: "async-error-flag-renderer" */ './ui/index.tsx'),
);

/**
 * @deprecated Do not use directly, instead use `JSErrorBoundary` in
 * `@atlassian/jira-error-boundaries`, which will log appropriate errors to
 * Sentry/SFX/Splunk. See https://hello.atlassian.net/wiki/spaces/JFP/pages/1890192830/Jira+Playbook+Uncaught+error+handling
 */
export const ErrorFlagRenderer = (props: ErrorFlagRendererProps): React.JSX.Element => {
	const packageDetails = {
		id: 'asyncErrorFlagComponent',
		packageName: 'jiraErrorBoundary',
	};

	return (
		<ErrorBoundary {...packageDetails}>
			<ReportErrors teamName="magma" {...packageDetails}>
				<Placeholder name="async-error-flag-renderer" fallback={null}>
					<ErrorFlagRendererLazy {...props} />
				</Placeholder>
			</ReportErrors>
		</ErrorBoundary>
	);
};

const DEFAULT_EXTRA_EVENT_DATA = {};

const renderErrorFlag = ({ error }: { error: Error }) => (
	<ErrorFlagRenderer id="error-boundary" error={error} />
);

const ErrorBoundaryFlagNew = ({
	children,
	extraEventData = DEFAULT_EXTRA_EVENT_DATA,
	id = undefined,
	...props
}: ErrorBoundaryFlagProps): React.JSX.Element => (
	<ErrorBoundary extraEventData={extraEventData} id={id} {...props} render={renderErrorFlag}>
		{children}
	</ErrorBoundary>
);

const ErrorBoundaryFlagOld = (props: ErrorBoundaryFlagProps): React.JSX.Element => {
	const { children, extraEventData, id, ...rest } = props;
	return (
		<ErrorBoundary extraEventData={extraEventData} id={id} {...rest} render={renderErrorFlag}>
			{children}
		</ErrorBoundary>
	);
};

ErrorBoundaryFlagOld.defaultProps = {
	extraEventData: DEFAULT_EXTRA_EVENT_DATA,
	id: undefined,
};

/**
 * An error boundary that will render an atlaskit flag when a render exception
 * is thrown.
 *
 * @deprecated Do not use directly, instead use `JSErrorBoundary` in
 * `@atlassian/jira-error-boundaries`, which will log appropriate errors to
 * Sentry/SFX/Splunk. See https://hello.atlassian.net/wiki/spaces/JFP/pages/1890192830/Jira+Playbook+Uncaught+error+handling
 */
export const ErrorBoundaryFlag: React.FC<ErrorBoundaryFlagProps> = componentWithFG(
	'jira_removing_defaultprops',
	ErrorBoundaryFlagNew,
	ErrorBoundaryFlagOld,
);
